export const innerCtas = {
  demo: { label: 'Register for Demo', to: '/register-for-demo' },
  sales: { label: 'Contact sales', to: '/contact-us/connect-with-us' },
};

const page = (data) => ({
  primary: innerCtas.demo,
  secondary: innerCtas.sales,
  ...data,
});

export const innerPages = {
  '/platforms': page({
    parent: 'Bright Study',
    kicker: 'PLATFORMS',
    title: 'One ecosystem for operations, learning, and growth',
    lead: 'Bright Study connects ERP, LMS, websites, AI, analytics, and mobile apps so school teams work from the same records.',
    image: 'platforms',
    links: [
      { label: 'ERP', text: 'Admissions, academics, fees, people, and campus operations in one system.', to: '/platforms/erp', icon: 'cog' },
      { label: 'LMS', text: 'Live classes, digital content, and assessments that follow the timetable.', to: '/platforms/lms', icon: 'book' },
      { label: 'Website', text: 'A modern school site that captures enquiries and stays in sync with the ERP.', to: '/platforms/website', icon: 'globe' },
      { label: 'AI', text: 'Assist teachers, parents, and leaders with answers, drafts, and early signals.', to: '/platforms/ai', icon: 'bolt' },
      { label: 'AI Analytics Dashboard', text: 'Live visibility across admissions, attendance, academics, and finance.', to: '/platforms/ai-analytics-dashboard', icon: 'chart' },
      { label: 'Mobile Application', text: 'Parents, teachers, and leadership stay connected from anywhere.', to: '/platforms/mobile-application', icon: 'phone' },
    ],
  }),

  '/platforms/erp': page({
    parent: 'Platforms',
    kicker: 'SCHOOL ERP',
    title: 'Run the whole campus from one record',
    lead: 'Replace scattered registers with a single flow for admissions, academics, fees, people, and daily operations.',
    image: 'intel',
    features: [
      { icon: 'school', title: 'Student information', text: 'One secure profile for documents, history, and classroom records.' },
      { icon: 'admin', title: 'Admissions', text: 'Follow every enquiry from first call to joining with a clear pipeline.' },
      { icon: 'quiz', title: 'Exams and results', text: 'Plan assessments and publish performance without parallel spreadsheets.' },
      { icon: 'finance', title: 'Fee and billing', text: 'Structures, collections, receipts, and outstanding risk in one place.' },
      { icon: 'people', title: 'People and HR', text: 'Staff records, attendance, and payroll inputs without file chasing.' },
      { icon: 'feed', title: 'Library and inventory', text: 'Catalogue, circulation, and stock without paper registers.' },
    ],
    outcomes: [
      { value: '1', label: 'Source of truth for student and fee data' },
      { value: '70%', label: 'Less admin follow-up after go-live' },
      { value: '24/7', label: 'Access for office, leadership, and staff' },
    ],
  }),

  '/platforms/lms': page({
    parent: 'Platforms',
    kicker: 'LEARNING PLATFORM',
    title: 'Turn study time into practice that sticks',
    lead: 'Give teachers a simple way to plan, share content, run assessments, and keep learning moving beyond the classroom.',
    image: 'learn',
    features: [
      { icon: 'video', title: 'Live classes', text: 'Connect scheduled sessions with Meet or Zoom from the school timetable.' },
      { icon: 'folder', title: 'Digital content', text: 'PDFs, video, audio, and notes students can revisit as often as they need.' },
      { icon: 'quiz', title: 'Smart assessment', text: 'Objective and subjective tests with faster evaluation workflows.' },
      { icon: 'book', title: 'Lesson planning', text: 'Keep classroom practice consistent across sections and campuses.' },
      { icon: 'school', title: 'Question banks', text: 'CBSE-aligned practice for competency-based patterns.' },
      { icon: 'chart', title: 'Learning signals', text: 'See who is practising, stuck, or ready for the next unit.' },
    ],
    outcomes: [
      { value: '1–10', label: 'Designed for growing K-12 classrooms' },
      { value: 'NEP', label: 'Activity-led work sits beside core practice' },
      { value: 'Live', label: 'Homework and content follow the timetable' },
    ],
  }),

  '/platforms/website': page({
    parent: 'Platforms',
    kicker: 'SCHOOL WEBSITE',
    title: 'A site that works as hard as admissions',
    lead: 'Fast, mobile-first pages that tell your story, publish notices, and capture every enquiry without waiting on a vendor ticket.',
    image: 'website',
    features: [
      { icon: 'devices', title: 'Responsive design', text: 'Clean pages on phones, tablets, and desktops from the first visit.' },
      { icon: 'feed', title: 'Dynamic content', text: 'Notices, galleries, calendars, and admissions stay in sync with the ERP.' },
      { icon: 'edit', title: 'CMS control', text: 'Update pages without a developer for every small change.' },
      { icon: 'search', title: 'SEO-ready structure', text: 'Help parents find the right campus with clear, indexed pages.' },
      { icon: 'admin', title: 'Enquiry forms', text: 'Structured follow-up so no admission lead sits unattended.' },
      { icon: 'shield', title: 'Accessible and secure', text: 'Inclusive browsing with reliable protection of school data.' },
    ],
    outcomes: [
      { value: 'Fast', label: 'Pages built for parent discovery' },
      { value: 'Live', label: 'Notices published from the same system' },
      { value: 'CMS', label: 'Staff can update without a ticket queue' },
    ],
  }),

  '/platforms/ai': page({
    parent: 'Platforms',
    kicker: 'AI FOR SCHOOLS',
    title: 'Act before small issues become campus-wide',
    lead: 'Bright Study AI helps teams draft, answer, and spot risk early — with teachers and leaders still in control of every decision.',
    image: 'ai',
    features: [
      { icon: 'chat', title: 'Campus assistant', text: '24/7 answers for common parent, student, and staff questions.' },
      { icon: 'book', title: 'Learning paths', text: 'Practice recommendations based on how each student is doing.' },
      { icon: 'quiz', title: 'Evaluation support', text: 'Faster first-pass grading with clearer feedback loops.' },
      { icon: 'shield', title: 'Integrity checks', text: 'Help protect originality across assignments and submissions.' },
      { icon: 'edit', title: 'Content support', text: 'Draft papers, notes, and practice sets with teacher review.' },
      { icon: 'chart', title: 'Behaviour signals', text: 'Engagement, attendance, and participation trends in one view.' },
    ],
    outcomes: [
      { value: 'Early', label: 'Risk detected while there is time to act' },
      { value: 'Teacher', label: 'AI assists, staff remain accountable' },
      { value: '24/7', label: 'Routine questions answered without a queue' },
    ],
  }),

  '/platforms/ai-analytics-dashboard': page({
    parent: 'Platforms',
    kicker: 'LEADERSHIP DASHBOARD',
    title: 'See every campus in one live view',
    lead: 'Admissions, attendance, academics, and finance stop living in month-end files. Principals and group heads act on the same numbers.',
    image: 'analytics',
    features: [
      { icon: 'chart', title: 'Executive snapshot', text: 'The four numbers leadership asks for, without waiting on a report.' },
      { icon: 'school', title: 'Academic forecasts', text: 'Estimate outcomes so interventions can be planned, not guessed.' },
      { icon: 'finance', title: 'Fee intelligence', text: 'Collection health and outstanding risk at a glance.' },
      { icon: 'people', title: 'Group visibility', text: 'Compare campuses without chasing each office for a spreadsheet.' },
      { icon: 'admin', title: 'Custom reports', text: 'Board packs and principal views from live operational data.' },
      { icon: 'bolt', title: 'Predictive alerts', text: 'Identify at-risk students before the term slips away.' },
    ],
    outcomes: [
      { value: 'Live', label: 'No monthly file chase for basic KPIs' },
      { value: 'Multi', label: 'Group and single-campus views' },
      { value: 'Board', label: 'Reports that match how leaders actually ask' },
    ],
  }),

  '/platforms/mobile-application': page({
    parent: 'Platforms',
    kicker: 'MOBILE APPS',
    title: 'The school in every pocket',
    lead: 'Parents, teachers, and leadership stay on the same circulars, fees, homework, and approvals — without extra apps for every task.',
    image: 'mobile',
    features: [
      { icon: 'phone', title: 'Parent app', text: 'Fees, homework, circulars, and transport updates from one login.' },
      { icon: 'book', title: 'Teacher app', text: 'Attendance, assignments, remarks, and class communication on the go.' },
      { icon: 'chart', title: 'Management app', text: 'Live snapshots of admissions, collections, and campus health.' },
      { icon: 'bus', title: 'Transport updates', text: 'Route visibility for families without extra GPS complexity.' },
      { icon: 'chat', title: 'Alerts that arrive', text: 'The same trusted updates parents already expect on SMS or WhatsApp.' },
      { icon: 'pay', title: 'Fee on the move', text: 'Pay and track dues without a campus visit for every receipt.' },
    ],
    outcomes: [
      { value: '1', label: 'Login for the updates families actually need' },
      { value: 'Less', label: 'Phone calls for routine circulars' },
      { value: 'Anywhere', label: 'Staff can work from campus or home' },
    ],
  }),

  '/school-optimisation': page({
    parent: 'Bright Study',
    kicker: 'SCHOOL OPTIMISATION',
    title: 'Give hours back to teaching and leadership',
    lead: 'Automate the work that used to live in registers, WhatsApp groups, and month-end files — with a clear audit trail.',
    image: 'optimisation',
    links: [
      { label: 'Administration', text: 'Admissions, certificates, attendance, and daily ops without paperwork piles.', to: '/school-optimisation/administration', icon: 'admin' },
      { label: 'Finance', text: 'Fee structures, collections, and reporting the board can trust.', to: '/school-optimisation/finance', icon: 'finance' },
      { label: 'Learning', text: 'Content, homework, and assessments that stay aligned with the classroom.', to: '/school-optimisation/learning', icon: 'book' },
      { label: 'Academics', text: 'Timetables, exams, and results in one academic flow.', to: '/school-optimisation/academics', icon: 'school' },
      { label: 'Intelligence', text: 'Live visibility so principals act on facts, not forwarded files.', to: '/school-optimisation/intelligence', icon: 'chart' },
      { label: 'Logistics', text: 'Transport, visitors, and campus movement with fewer manual checks.', to: '/school-optimisation/logistics', icon: 'bus' },
      { label: 'Leadership / Management', text: 'Approvals and snapshots for people who cannot sit in the front office.', to: '/school-optimisation/leadership-management', icon: 'people' },
      { label: 'Enterprise Features', text: 'Multi-campus control, roles, and consistency across a school group.', to: '/school-optimisation/enterprise-features', icon: 'cog' },
      { label: 'Communication', text: 'Circulars and alerts on the channels families already use.', to: '/school-optimisation/communication', icon: 'chat' },
      { label: 'Human Resources', text: 'Staff records, attendance, and payroll inputs in one place.', to: '/school-optimisation/human-resources', icon: 'badge' },
      { label: 'Explore Automation', text: 'See how repetitive school work can run every day without chasing.', to: '/school-optimisation/explore-automation', icon: 'bolt' },
    ],
  }),

  '/school-optimisation/administration': page({
    parent: 'School Optimisation',
    kicker: 'ADMINISTRATION',
    title: 'Cut the paperwork around daily school ops',
    lead: 'Admissions, attendance, certificates, and front-office follow-ups sit in one flow so the office stops running after files.',
    image: 'administration',
    features: [
      { icon: 'admin', title: 'Enquiry to admission', text: 'A structured path from first interest to confirmed joining.' },
      { icon: 'feed', title: 'Certificates', text: 'Bonafide, transfer, and character letters without reprinting piles.' },
      { icon: 'school', title: 'Student records', text: 'Documents and history stay attached to the right profile.' },
      { icon: 'badge', title: 'Attendance capture', text: 'Presence that flows into reports without a second register.' },
      { icon: 'search', title: 'Front-office search', text: 'Find a student, fee, or request without opening five folders.' },
      { icon: 'shield', title: 'Role-based access', text: 'Staff see only the records their role should see.' },
    ],
    outcomes: [
      { value: 'Less', label: 'File chasing for routine certificates' },
      { value: 'Clear', label: 'Admission pipeline for every lead' },
      { value: 'Audit', label: 'Who changed what, and when' },
    ],
  }),

  '/school-optimisation/finance': page({
    parent: 'School Optimisation',
    kicker: 'FINANCE',
    title: 'Collections the office and board can both trust',
    lead: 'Fee structures, receipts, concessions, and outstanding risk stop living in separate sheets for every class.',
    image: 'finance',
    features: [
      { icon: 'finance', title: 'Fee structures', text: 'Class, optional, and transport fees without duplicate masters.' },
      { icon: 'pay', title: 'Online collection', text: 'Gateways and QR so parents can pay without a campus visit.' },
      { icon: 'feed', title: 'Receipts', text: 'Instant records that match what leadership sees in the dashboard.' },
      { icon: 'chart', title: 'Outstanding risk', text: 'See who is late while there is still time to follow up calmly.' },
      { icon: 'admin', title: 'Concessions', text: 'Approvals with a trail, not a side conversation.' },
      { icon: 'search', title: 'Finance reports', text: 'The views accounts and principals actually ask for.' },
    ],
    outcomes: [
      { value: 'Live', label: 'Collection health without month-end wait' },
      { value: 'One', label: 'Structure for every campus and class' },
      { value: 'Clear', label: 'Receipts parents and auditors can match' },
    ],
  }),

  '/school-optimisation/learning': page({
    parent: 'School Optimisation',
    kicker: 'LEARNING',
    title: 'Keep practice, homework, and content in one loop',
    lead: 'Teachers assign, students practise, and coordinators see progress without a second set of tools.',
    image: 'learning',
    features: [
      { icon: 'book', title: 'Homework flow', text: 'Assign, submit, and remark without a pile of notebooks at the gate.' },
      { icon: 'folder', title: 'Resource library', text: 'Curated material so staff are not starting from a blank page.' },
      { icon: 'quiz', title: 'Classroom assessment', text: 'Short checks that feed the same record as term exams.' },
      { icon: 'video', title: 'Revision content', text: 'Students can revisit what they missed in class.' },
      { icon: 'chart', title: 'Completion signals', text: 'See who is behind before the unit test arrives.' },
      { icon: 'people', title: 'Parent visibility', text: 'Families see work and remarks without calling the class teacher.' },
    ],
    outcomes: [
      { value: 'One', label: 'Loop for assign, practise, and review' },
      { value: 'Less', label: 'WhatsApp homework chaos' },
      { value: 'Clear', label: 'Progress for coordinators and parents' },
    ],
  }),

  '/school-optimisation/academics': page({
    parent: 'School Optimisation',
    kicker: 'ACADEMICS',
    title: 'Timetables, exams, and results in a single flow',
    lead: 'Academic operations stay connected to attendance, LMS, and report cards so sections do not drift apart.',
    image: 'academics',
    features: [
      { icon: 'school', title: 'Timetable', text: 'Periods, substitutions, and teacher load without wall charts.' },
      { icon: 'quiz', title: 'Exam planning', text: 'Schedules, rooms, and marks entry in one academic calendar.' },
      { icon: 'edit', title: 'Report cards', text: 'Publish results that match what was actually assessed.' },
      { icon: 'book', title: 'Syllabus tracking', text: 'See coverage across sections before the term ends.' },
      { icon: 'people', title: 'Teacher allocation', text: 'Who is teaching what, with fewer last-minute clashes.' },
      { icon: 'chart', title: 'Academic insights', text: 'Class and student performance without a manual marksheet merge.' },
    ],
    outcomes: [
      { value: 'Aligned', label: 'Classroom, exam, and report card data' },
      { value: 'Faster', label: 'Result publishing with fewer errors' },
      { value: 'Fair', label: 'Load and coverage visible to coordinators' },
    ],
  }),

  '/school-optimisation/intelligence': page({
    parent: 'School Optimisation',
    kicker: 'INTELLIGENCE',
    title: 'Lead with numbers the front office already uses',
    lead: 'Admissions, attendance, academics, and finance sit in one view so decisions are not waiting on a forwarded file.',
    image: 'intelligence',
    features: [
      { icon: 'chart', title: 'Campus snapshot', text: 'The daily picture principals need before the first meeting.' },
      { icon: 'bolt', title: 'Early risk', text: 'Attendance, fee, and learning signals before they become a crisis.' },
      { icon: 'people', title: 'Group comparison', text: 'See campuses side by side without a monthly consolidation.' },
      { icon: 'finance', title: 'Collection pulse', text: 'Know where follow-up still matters this week.' },
      { icon: 'school', title: 'Academic pulse', text: 'Spot classes that need support while the term can still change.' },
      { icon: 'admin', title: 'Export for boards', text: 'Packs that start from live data, not a rebuilt spreadsheet.' },
    ],
    outcomes: [
      { value: 'Same', label: 'Numbers for office and leadership' },
      { value: 'Early', label: 'Time to intervene, not just report' },
      { value: 'Calm', label: 'Fewer surprises in review meetings' },
    ],
  }),

  '/school-optimisation/logistics': page({
    parent: 'School Optimisation',
    kicker: 'LOGISTICS',
    title: 'Move students, staff, and visitors with less friction',
    lead: 'Transport, gate, and campus movement stay connected to the same student record the office already trusts.',
    image: 'logistics',
    features: [
      { icon: 'bus', title: 'Routes and trips', text: 'Plan transport without a separate binder for every vehicle.' },
      { icon: 'pin', title: 'Family updates', text: 'Give parents visibility without extra hardware complexity.' },
      { icon: 'badge', title: 'Staff attendance', text: 'RFID or face capture that writes into HR records automatically.' },
      { icon: 'shield', title: 'Visitor flow', text: 'QR check-in so the gate is not a paper register.' },
      { icon: 'people', title: 'Student movement', text: 'Know who is on campus with the same profile used in class.' },
      { icon: 'chat', title: 'Exception alerts', text: 'Tell families about delays without a phone tree.' },
    ],
    outcomes: [
      { value: 'Safer', label: 'Gate and transport tied to student records' },
      { value: 'Fewer', label: 'Manual registers at the boundary' },
      { value: 'Clear', label: 'Updates when a route runs late' },
    ],
  }),

  '/school-optimisation/leadership-management': page({
    parent: 'School Optimisation',
    kicker: 'LEADERSHIP',
    title: 'Approvals and visibility without sitting in the office',
    lead: 'Principals and group heads get the snapshots and sign-offs they need — on campus, at home, or between sites.',
    image: 'leadership',
    features: [
      { icon: 'chart', title: 'Morning briefing', text: 'Admissions, collections, and attendance before the first period.' },
      { icon: 'admin', title: 'Approvals', text: 'Concessions, leave, and requests with a trail, not a hallway yes.' },
      { icon: 'people', title: 'Campus health', text: 'See which teams are blocked without waiting for a verbal update.' },
      { icon: 'phone', title: 'On-the-go access', text: 'The same view on mobile that the desktop dashboard shows.' },
      { icon: 'shield', title: 'Accountable access', text: 'Leaders see more, with an audit of sensitive actions.' },
      { icon: 'feed', title: 'Escalations', text: 'The exceptions that actually need a principal, not every circular.' },
    ],
    outcomes: [
      { value: 'Faster', label: 'Sign-offs without a paper pile' },
      { value: 'Same', label: 'View on desk and phone' },
      { value: 'Fewer', label: 'Surprises in the weekly review' },
    ],
  }),

  '/school-optimisation/enterprise-features': page({
    parent: 'School Optimisation',
    kicker: 'ENTERPRISE',
    title: 'One process across every campus in the group',
    lead: 'Franchise and multi-school networks stay consistent — without forcing every campus to invent its own spreadsheet.',
    image: 'enterprise',
    features: [
      { icon: 'cog', title: 'Shared masters', text: 'Fee, academic, and role templates that travel with the group.' },
      { icon: 'people', title: 'Central office view', text: 'See progress without chasing each school for a file.' },
      { icon: 'shield', title: 'Role design', text: 'Campus staff and group staff see the right slice of data.' },
      { icon: 'chart', title: 'Benchmarking', text: 'Compare campuses on the same definitions of attendance and collection.' },
      { icon: 'admin', title: 'Controlled rollout', text: 'Turn modules on when a campus is ready, not all at once.' },
      { icon: 'globe', title: 'Brand consistency', text: 'Parent apps and websites stay on-message across locations.' },
    ],
    outcomes: [
      { value: 'One', label: 'Operating language for every campus' },
      { value: 'Fair', label: 'Comparisons on the same metrics' },
      { value: 'Ready', label: 'Rollout that matches each school’s pace' },
    ],
  }),

  '/school-optimisation/communication': page({
    parent: 'School Optimisation',
    kicker: 'COMMUNICATION',
    title: 'Reach families on the channels they already open',
    lead: 'Circulars, remarks, and alerts leave the office once and arrive on app, SMS, or WhatsApp without a second typing job.',
    image: 'communication',
    features: [
      { icon: 'chat', title: 'WhatsApp and SMS', text: 'The same trusted school update, without a parallel message list.' },
      { icon: 'feed', title: 'Circulars', text: 'Publish once. Classes and sections receive the right version.' },
      { icon: 'book', title: 'Daily remarks', text: 'Teachers share classroom notes parents can actually find later.' },
      { icon: 'phone', title: 'App inbox', text: 'A history of school messages, not a vanishing chat scroll.' },
      { icon: 'admin', title: 'Targeted groups', text: 'Class, bus, or whole school — without accidental all-hands noise.' },
      { icon: 'shield', title: 'Record of send', text: 'Know what left the office when a parent says they never got it.' },
    ],
    outcomes: [
      { value: 'Once', label: 'Typed in the office, delivered everywhere' },
      { value: 'Fewer', label: 'Calls for routine circulars' },
      { value: 'Clear', label: 'History when something is disputed' },
    ],
  }),

  '/school-optimisation/human-resources': page({
    parent: 'School Optimisation',
    kicker: 'HUMAN RESOURCES',
    title: 'Staff records that keep up with the academic year',
    lead: 'Recruitment, attendance, leave, and payroll inputs sit with the same people data classrooms already depend on.',
    image: 'hr',
    features: [
      { icon: 'people', title: 'Staff profiles', text: 'Documents, roles, and campus assignment in one record.' },
      { icon: 'badge', title: 'Attendance', text: 'Capture that flows into payroll inputs without a second register.' },
      { icon: 'admin', title: 'Leave', text: 'Requests and balances with a visible approval path.' },
      { icon: 'search', title: 'Recruitment notes', text: 'Keep hiring conversations attached to the candidate, not a chat.' },
      { icon: 'finance', title: 'Payroll inputs', text: 'The numbers accounts need, from the attendance the school already took.' },
      { icon: 'shield', title: 'Confidential access', text: 'HR data stays with the roles that should see it.' },
    ],
    outcomes: [
      { value: 'One', label: 'Staff record for ops and classroom allocation' },
      { value: 'Fewer', label: 'Payroll mismatches at month end' },
      { value: 'Clear', label: 'Leave without hallway approvals' },
    ],
  }),

  '/school-optimisation/explore-automation': page({
    parent: 'School Optimisation',
    kicker: 'AUTOMATION',
    title: 'Build the process once. Let it run every day.',
    lead: 'Admissions, fees, attendance, and communication can follow the same path campus after campus — with a trail you can audit.',
    image: 'auto',
    features: [
      { icon: 'cog', title: 'Repeatable workflows', text: 'Stop reinventing admission and fee follow-up each term.' },
      { icon: 'bolt', title: 'Triggered alerts', text: 'The right person is notified when a step is waiting.' },
      { icon: 'admin', title: 'Less re-entry', text: 'Data typed once shows up in reports, apps, and certificates.' },
      { icon: 'chart', title: 'Bottleneck views', text: 'See where requests stall instead of discovering it in a meeting.' },
      { icon: 'shield', title: 'Accountability', text: 'Every automated step still has a name and a timestamp.' },
      { icon: 'people', title: 'Time back to teaching', text: 'Office teams spend hours on exceptions, not every routine file.' },
    ],
    outcomes: [
      { value: 'Daily', label: 'Processes that do not wait for a reminder' },
      { value: 'Audit', label: 'Who did what, including automated steps' },
      { value: 'Focus', label: 'Staff time on growth, not follow-ups' },
    ],
  }),

  '/success-stories': page({
    parent: 'Bright Study',
    kicker: 'SUCCESS STORIES',
    title: 'What changes when a school stops managing chaos',
    lead: 'Connected campuses cut follow-ups, give teachers time back, and let leadership see the same numbers the office uses.',
    image: 'success',
    stories: true,
  }),

  '/success-stories/case-studies': page({
    parent: 'Success Stories',
    kicker: 'CASE STUDIES',
    title: 'From scattered files to one operating rhythm',
    lead: 'These snapshots show how single campuses and school groups use Bright Study to replace month-end reconstruction with live operations.',
    image: 'cases',
    stories: true,
  }),

  '/insights': page({
    parent: 'Bright Study',
    kicker: 'INSIGHTS',
    title: 'Ideas for teams building future-ready schools',
    lead: 'Short reads, talks, and classroom notes for leaders, teachers, and families — without the noise of a generic education blog.',
    image: 'insights',
    badge: { value: 'Read', label: 'Ideas for teams building future-ready schools' },
    links: [
      { label: 'Coverage', text: 'How connected operations show up in the wider education conversation.', to: '/insights/coverage', icon: 'search' },
      { label: 'Video', text: 'Walkthroughs and campus stories you can share with the leadership team.', to: '/insights/video', icon: 'video' },
      { label: 'Ed Talks', text: 'Conversations on automation, AI, and the work of running a school.', to: '/insights/ed-talks', icon: 'chat' },
      { label: 'Blogs', text: 'Practical notes from admissions, academics, and parent communication.', to: '/insights/blogs', icon: 'edit' },
      { label: 'Newsletter', text: 'A short briefing for people who do not have time for another feed.', to: '/insights/newsletter', icon: 'feed' },
      { label: 'Student Zone', text: 'Practice habits and exam-week reminders written for learners.', to: '/insights/student-zone', icon: 'school' },
      { label: 'Educator Zone', text: 'Classroom workflows that respect a teacher’s actual day.', to: '/insights/educator-zone', icon: 'book' },
      { label: 'Educators Article', text: 'Longer pieces on assessment, NEP practice, and staff adoption.', to: '/insights/educators-article', icon: 'folder' },
    ],
  }),

  '/insights/coverage': page({
    parent: 'Insights',
    kicker: 'COVERAGE',
    title: 'Bright Study in the education conversation',
    lead: 'A selection of themes we are exploring with school leaders — operations, parent trust, and AI used with adult judgement.',
    image: 'coverage',
    badge: { value: 'Press', label: 'Operations, parent trust, and AI with judgement' },
    articles: [
      { tag: 'Operations', title: 'Why fee leakage is usually a process problem', text: 'When collection, concessions, and receipts live in different files, the gap is operational — not just a payment gateway.' },
      { tag: 'Leadership', title: 'The dashboard a principal will actually open', text: 'Four numbers in the morning beat a 40-tab workbook that arrives on Friday evening.' },
      { tag: 'Parents', title: 'Trust is a message log', text: 'Families calm down when they can find last week’s circular without calling the front office.' },
    ],
  }),

  '/insights/video': page({
    parent: 'Insights',
    kicker: 'VIDEO',
    title: 'See the workflow, not just the slide',
    lead: 'Short walkthroughs for leadership teams who want to understand admissions, fees, and classroom loops before a demo.',
    image: 'video',
    badge: { value: 'Watch', label: 'See the workflow, not just the slide' },
    articles: [
      { tag: 'Walkthrough', title: 'From enquiry to joining in one path', text: 'How a counsellor, accounts, and the principal stay on the same admission record.' },
      { tag: 'Product', title: 'A morning in the leadership dashboard', text: 'Attendance, collections, and academic flags before the first period starts.' },
      { tag: 'Classroom', title: 'Homework without a second app', text: 'Assign, submit, and remark in the same place parents already check fees.' },
    ],
  }),

  '/insights/ed-talks': page({
    parent: 'Insights',
    kicker: 'ED TALKS',
    title: 'Conversations for people who run schools',
    lead: 'Automation, AI, and parent communication — discussed as operational choices, not technology fashion.',
    image: 'talks',
    badge: { value: 'Talks', label: 'Conversations for people who run schools' },
    articles: [
      { tag: 'Talk', title: 'What to automate first in a growing campus', text: 'Start where follow-ups hurt: admissions, fees, and circulars — then expand.' },
      { tag: 'Talk', title: 'AI that teachers will allow in the room', text: 'Drafting help and early signals, with staff still accountable for marks and messages.' },
      { tag: 'Talk', title: 'Multi-campus without losing the school’s voice', text: 'Shared processes can still leave room for how each principal leads.' },
    ],
  }),

  '/insights/blogs': page({
    parent: 'Insights',
    kicker: 'BLOGS',
    title: 'Practical notes from the school week',
    lead: 'Short pieces on the work that actually fills an office and a staffroom — written for people short on time.',
    image: 'blogs',
    badge: { value: 'Notes', label: 'Practical reads from the school week' },
    articles: [
      { tag: 'Admissions', title: 'Stop losing enquiries at the first callback', text: 'A shared pipeline beats a counsellor’s personal notebook when two people are on leave.' },
      { tag: 'Academics', title: 'Report cards should not be a reconstruction project', text: 'If marks live in the same flow as the exam, publishing is a check, not a rebuild.' },
      { tag: 'Parents', title: 'One inbox for the messages that matter', text: 'Circulars vanish in chat. A school inbox still has last Tuesday’s transport note.' },
      { tag: 'People', title: 'Staff attendance is an HR file and a classroom file', text: 'When those diverge, substitutions and payroll both become arguments.' },
    ],
  }),

  '/insights/newsletter': page({
    parent: 'Insights',
    kicker: 'NEWSLETTER',
    title: 'A short briefing for school teams',
    lead: 'Product notes, classroom workflows, and leadership prompts — once in a while, not every morning.',
    image: 'newsletter',
    badge: { value: 'Inbox', label: 'A short briefing, not another feed' },
    form: 'newsletter',
    articles: [
      { tag: 'Briefing', title: 'What we send', text: 'Go-live lessons, module updates, and one operational idea you can try the same week.' },
      { tag: 'Audience', title: 'Who it is for', text: 'Principals, coordinators, and admin leads who do not want another social feed.' },
    ],
  }),

  '/insights/student-zone': page({
    parent: 'Insights',
    kicker: 'STUDENT ZONE',
    title: 'Practice habits that survive a busy week',
    lead: 'Reminders for learners who want to improve without turning study into another source of panic.',
    image: 'students',
    badge: { value: 'Learn', label: 'Practice habits that survive a busy week' },
    articles: [
      { tag: 'Practice', title: 'Twenty focused minutes beat a late-night marathon', text: 'Short, regular quizzes build recall. Cramming only feels like work.' },
      { tag: 'Exams', title: 'Read the question the way the paper means it', text: 'Competency questions reward showing the step, not only the final number.' },
      { tag: 'Wellbeing', title: 'Sleep is part of revision', text: 'A tired brain does not store yesterday’s practice. Protect the night before the test.' },
    ],
  }),

  '/insights/educator-zone': page({
    parent: 'Insights',
    kicker: 'EDUCATOR ZONE',
    title: 'Classroom workflows that respect the actual day',
    lead: 'Notes for teachers and coordinators who need tools that vanish into the period, not a second job after school.',
    image: 'educators',
    badge: { value: 'Teach', label: 'Workflows that respect the actual school day' },
    articles: [
      { tag: 'Classroom', title: 'Attendance in the first three minutes', text: 'If capture is slow, everything else in the period starts late.' },
      { tag: 'Feedback', title: 'Remarks parents can act on', text: 'One specific next step beats a paragraph of general encouragement.' },
      { tag: 'Adoption', title: 'If it needs a training week, it will not stick', text: 'The interface has to feel like email, or staff will keep the paper register.' },
    ],
  }),

  '/insights/educators-article': page({
    parent: 'Insights',
    kicker: 'EDUCATORS ARTICLE',
    title: 'Longer reads for academic leads',
    lead: 'Assessment design, NEP-aligned practice, and staff adoption — for coordinators who need more than a checklist.',
    image: 'articles',
    badge: { value: 'Deep', label: 'Longer reads for academic leads' },
    articles: [
      { tag: 'Assessment', title: 'When the question bank and the report card disagree', text: 'Competency practice only works if the published result uses the same language.' },
      { tag: 'NEP', title: 'Activity is not extra if it is in the plan', text: 'Experiential work needs a slot, a resource, and a way to record what happened.' },
      { tag: 'Change', title: 'Pilot one section before you declare a digital campus', text: 'A clean go-live in three classes beats a noisy launch in thirty.' },
    ],
  }),

  '/about-us': page({
    parent: 'Bright Study',
    kicker: 'ABOUT BRIGHT STUDY',
    title: 'School systems built for the people who use them every day',
    lead: 'Bright Study exists so teachers, office teams, and principals can share one record — and spend less of the week reconstructing last month.',
    image: 'about',
    badge: { value: 'Bright Study', label: 'School systems built for daily campus work' },
    links: [
      { label: 'Company', text: 'Why we build connected school software, and how we work with campuses.', to: '/about-us/company', icon: 'globe' },
      { label: 'Leadership', text: 'The people accountable for product, delivery, and customer success.', to: '/about-us/leadership', icon: 'people' },
      { label: 'Partners', text: 'Payments, cloud, and communication services that sit beside the ERP.', to: '/about-us/partners', icon: 'cog' },
      { label: 'Awards', text: 'Recognition for making school operations calmer and more accountable.', to: '/about-us/awards', icon: 'badge' },
    ],
  }),

  '/about-us/company': page({
    parent: 'About Us',
    kicker: 'COMPANY',
    title: 'Clarity for growing schools',
    lead: 'We design Bright Study for CBSE and similar K-12 campuses that have outgrown notebooks, chat groups, and a graveyard of logins.',
    image: 'company',
    badge: { value: 'K-12', label: 'Designed around growing Indian campuses' },
    features: [
      { icon: 'school', title: 'Built for school work', text: 'The interface assumes a counsellor, accountant, and class teacher — not an IT department.' },
      { icon: 'cog', title: 'One ecosystem', text: 'ERP, LMS, website, AI, and mobile share records instead of exporting CSVs at night.' },
      { icon: 'shield', title: 'Accountable by default', text: 'Roles, backups, and an audit trail are part of the product, not a later add-on.' },
      { icon: 'people', title: 'Stay after go-live', text: 'Support for school teams and parents when logins, fees, or cutover questions appear.' },
      { icon: 'globe', title: 'Cloud delivery', text: 'Work from campus, home, or a second branch without a VPN drama.' },
      { icon: 'bolt', title: 'Improve in public', text: 'Modules ship when they reduce follow-ups, not when a slide looks complete.' },
    ],
    outcomes: [
      { value: 'K-12', label: 'Designed around growing Indian campuses' },
      { value: 'One', label: 'Record for office, classroom, and leadership' },
      { value: 'Stay', label: 'Support after the first week, not only at sale' },
    ],
  }),

  '/about-us/leadership': page({
    parent: 'About Us',
    kicker: 'LEADERSHIP',
    title: 'People accountable for the product schools live in',
    lead: 'Bright Study is led by operators who care about adoption, data care, and whether a principal will open the dashboard on a Tuesday morning.',
    image: 'aboutTeam',
    badge: { value: 'Team', label: 'Accountable for product, delivery, and success' },
    people: [
      { name: 'Product', role: 'Product and engineering', text: 'Keeps ERP, LMS, and mobile on the same student record, with fewer screens for the same job.' },
      { name: 'Delivery', role: 'Implementation', text: 'Owns go-live, training, and the unglamorous work of a clean data cutover.' },
      { name: 'Success', role: 'School support', text: 'Helps campus teams and parents after launch — logins, fees, and day-two questions.' },
    ],
  }),

  '/about-us/partners': page({
    parent: 'About Us',
    kicker: 'PARTNERS',
    title: 'The services that sit beside Bright Study',
    lead: 'Schools already have payment, messaging, and hosting needs. We connect those jobs to the same campus record instead of adding another island.',
    image: 'aboutPartners',
    badge: { value: 'Connect', label: 'Payments, cloud, and messaging beside the ERP' },
    features: [
      { icon: 'pay', title: 'Payments', text: 'Fee gateways and QR collection that write back into ledgers automatically.' },
      { icon: 'chat', title: 'Messaging', text: 'SMS and WhatsApp for the circulars families already expect.' },
      { icon: 'globe', title: 'Cloud hosting', text: 'Reliable delivery with backups schools can restore from.' },
      { icon: 'video', title: 'Live class tools', text: 'Meet and Zoom sessions that follow the timetable, not a separate roster.' },
      { icon: 'shield', title: 'Identity and access', text: 'OTP and role controls for the people who hold school data.' },
      { icon: 'devices', title: 'Devices and capture', text: 'Optional RFID or face attendance that feeds HR and classroom records.' },
    ],
    outcomes: [
      { value: 'Connect', label: 'Partner tools write into Bright Study, not around it' },
      { value: 'Choice', label: 'Schools keep the providers they already trust' },
      { value: 'One', label: 'Student record underneath every add-on' },
    ],
  }),

  '/about-us/awards': page({
    parent: 'About Us',
    kicker: 'AWARDS',
    title: 'Recognition for calmer school operations',
    lead: 'We measure success by hours returned to teaching and by whether leadership trusts the numbers — awards are a by-product of that work.',
    image: 'aboutAwards',
    badge: { value: 'Trust', label: 'Recognition for calmer school operations' },
    articles: [
      { tag: 'Operations', title: 'What we measure', text: 'Fewer file-chases in admissions, fees, and certificates on growing campuses.' },
      { tag: 'Product', title: 'What we ship for', text: 'LMS practice that stays attached to the same academic calendar as exams.' },
      { tag: 'Trust', title: 'What leadership needs', text: 'Role-based access, backups, and an audit trail — even when parents never see it.' },
    ],
  }),

  '/contact-us': page({
    parent: 'Bright Study',
    kicker: 'CONTACT',
    title: 'Talk to the team that will still be here after go-live',
    lead: 'Demos, partnerships, speaking, and careers — pick the path that matches what you need from Bright Study.',
    image: 'contact',
    badge: { value: 'Hello', label: 'Sales, support, and go-live in one place' },
    links: [
      { label: 'Connect With Us', text: 'Questions about modules, pricing conversations, or a campus walkthrough.', to: '/contact-us/connect-with-us', icon: 'chat' },
      { label: 'Apply As A Speaker', text: 'Join an Bright Study conversation on operations, AI, or classroom practice.', to: '/contact-us/apply-as-a-speaker', icon: 'video' },
      { label: 'Career', text: 'Build school software with people who care about adoption, not just launches.', to: '/contact-us/career', icon: 'people' },
    ],
    form: 'contact',
  }),

  '/contact-us/connect-with-us': page({
    parent: 'Contact Us',
    kicker: 'CONNECT WITH US',
    title: 'Tell us about your campus',
    lead: 'Share a few details and we will route you to a walkthrough, a sales conversation, or the right specialist.',
    image: 'connect',
    badge: { value: 'Talk', label: 'We route every note to the right specialist' },
    form: 'contact',
  }),

  '/contact-us/apply-as-a-speaker': page({
    parent: 'Contact Us',
    kicker: 'SPEAK WITH BRIGHT STUDY',
    title: 'Bring a school story to the room',
    lead: 'We host conversations on automation, AI, and the unglamorous work of running a campus. Propose a topic and how you have lived it.',
    image: 'speaker',
    badge: { value: 'Live', label: 'Short talks, panels, and campus stories' },
    form: 'speaker',
    articles: [
      { tag: 'Fit', title: 'What we look for', text: 'Operators and educators with a specific story — not a generic keynote on “the future of education”.' },
      { tag: 'Format', title: 'How sessions run', text: 'Short talks, panels, and campus case conversations. We will follow up if there is a slot.' },
    ],
  }),

  '/contact-us/career': page({
    parent: 'Contact Us',
    kicker: 'CAREERS',
    title: 'Build tools schools will still use in year three',
    lead: 'We hire people who care about go-live, data care, and whether a teacher will open the app on a busy Monday.',
    image: 'career',
    badge: { value: 'Join', label: 'Build tools schools still use in year three' },
    form: 'career',
    articles: [
      { tag: 'Product', title: 'Design and engineering', text: 'Make dense school workflows feel like email — and keep ERP, LMS, and mobile on one record.' },
      { tag: 'Success', title: 'Implementation', text: 'Cutover, training, and parent help-desk work for campuses going live.' },
      { tag: 'GTM', title: 'School conversations', text: 'Talk to principals and group heads like operators, not like a slide deck.' },
    ],
  }),
};
